import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Clock, Loader2, RotateCcw, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProjectContext } from '../../../contexts/ProjectContext';
import { useChatScroll } from '../../../hooks/useChatScroll';
import { bareChatId, useChatHistory } from '../../../hooks/useConversation';
import { useShanksChatConnect } from '../../../hooks/useShanksChatConnect';
import { ChatHistoryUnavailable, ChatTurnError } from '../../ChatView/ChatRecovery';
import { ChatThread } from '../../ChatView/ChatThread';
import { convertShanksToChatMessages, mergeMessagesById } from '../../ChatView/shanksMessageAdapter';
import { ComposerInput } from '../../ComposerInput';
import { useProposalV2Context } from './ProposalV2Context';
import { SidebarChatHistory } from './SidebarChatHistory';
/**
 * AI chat sidebar for the V2 proposal dashboard.
 *
 * Composes from useShanksChat + ChatThread + ComposerInput directly
 * (same primitives as ChatView, without the header/settings/share chrome).
 * Includes a slide-over history panel.
 */ export function AISidebarShell({ embedded = false }) {
    const { aiContext, setAiContext, setSidebarOpen, activeChatId, setActiveChatId, conversationStarted, setConversationStarted } = useProposalV2Context();
    const { projectId, canEdit } = useProjectContext();
    const [showHistory, setShowHistory] = useState(false);
    const { scrollRef, awayFromBottom, jumpToLatest } = useChatScroll(activeChatId);
    const shanksConversationId = bareChatId(activeChatId);
    const history = useChatHistory(activeChatId, !conversationStarted);
    const { data: historyData, isLoading: historyLoading } = history;
    const readOnly = !canEdit || !!historyData?.readOnly;
    const rememberConversation = useCallback(()=>setConversationStarted(true), [
        setConversationStarted
    ]);
    const chatEnvelope = useMemo(()=>{
        const volumeId = aiContext?.volumeId;
        return {
            surface: volumeId ? 'proposal_document' : 'proposal',
            projectId: projectId ?? undefined,
            volumeId
        };
    }, [
        aiContext?.volumeId,
        projectId
    ]);
    const savedSubmission = historyData?.submissions?.find((s)=>s.state === 'running') ?? historyData?.submissions?.[0];
    const restoredSubmission = useMemo(()=>savedSubmission?.message ? {
            id: savedSubmission.submissionId,
            content: savedSubmission.message,
            fileIds: savedSubmission.fileIds,
            state: savedSubmission.state
        } : undefined, [
        savedSubmission
    ]);
    const shanks = useShanksChatConnect({
        chatId: activeChatId,
        initialConversationId: shanksConversationId,
        projectId: projectId ?? undefined,
        context: chatEnvelope,
        onConversationId: rememberConversation,
        onFinish: ()=>void history.refetch(),
        onError: ()=>void history.refetch(),
        restoredSubmission
    });
    const responseBusy = shanks.isLoading || !readOnly && savedSubmission?.state === 'running';
    const unfinished = !readOnly && savedSubmission && [
        'incomplete',
        'failed',
        'interrupted'
    ].includes(savedSubmission.state);
    const incomplete = shanks.incomplete || !shanks.error && unfinished && savedSubmission.state === 'incomplete';
    const turnError = responseBusy ? null : shanks.error ?? (unfinished ? incomplete ? 'This request needs more work. Your progress is saved. Continue to finish it.' : 'This response was interrupted. Retry your saved request.' : null);
    // History messages come pre-formatted as ChatMessage[] from the API.
    // Live messages from useShanksChat need conversion.
    const historyMessages = useMemo(()=>historyData?.messages ?? [], [
        historyData
    ]);
    const liveMessages = useMemo(()=>convertShanksToChatMessages(shanks.messages), [
        shanks.messages
    ]);
    const messages = useMemo(()=>mergeMessagesById(historyMessages, liveMessages, shanks.isLoading), [
        historyMessages,
        liveMessages,
        shanks.isLoading
    ]);
    // Auto-send message when aiContext.autoMessage is set (e.g., outline prefill).
    const autoMessageSent = useRef(false);
    useEffect(()=>{
        if (aiContext?.autoMessage && !autoMessageSent.current && !responseBusy && !historyLoading && !history.isError && !readOnly) {
            autoMessageSent.current = true;
            const msg = aiContext.autoMessage;
            // Clear autoMessage so it doesn't re-send on re-render.
            setAiContext({
                ...aiContext,
                autoMessage: undefined
            });
            shanks.sendMessage(aiContext.label ? `[Context: ${aiContext.label}] ${msg}` : msg);
        }
    }, [
        aiContext,
        shanks,
        responseBusy,
        setAiContext,
        history.isError,
        historyLoading,
        readOnly
    ]);
    // Accept/Reject/Comment on a DocumentWriteCard continues this conversation.
    // Scoped to THIS sidebar's chat via ChatThread so the follow-up never leaks
    // into another chat surface mounted at the same time.
    const sendCardFeedback = useCallback((message)=>{
        if (!readOnly && !historyLoading && !history.isError && !responseBusy) shanks.sendMessage(message);
    }, [
        shanks,
        responseBusy,
        readOnly,
        historyLoading,
        history.isError
    ]);
    // Document content reading is now handled server-side via zou → baltigo.
    // The read-document-content tool calls GetVolumeDocumentContent RPC directly.
    const handleSubmit = useCallback(async (data)=>{
        let content = data.prompt.trim();
        if (!content || readOnly || historyLoading || history.isError || responseBusy) return;
        if (aiContext?.label) {
            content = `[Context: ${aiContext.label}] ${content}`;
        }
        const fileIds = data.attachments?.map((a)=>a.id).filter((id)=>!!id);
        await shanks.sendMessage(content, {
            fileIds
        });
        jumpToLatest();
    }, [
        shanks,
        aiContext,
        history.isError,
        historyLoading,
        jumpToLatest,
        readOnly,
        responseBusy
    ]);
    const handleNewChat = useCallback(()=>{
        shanks.reset();
        setConversationStarted(false);
        setActiveChatId(crypto.randomUUID());
    }, [
        shanks,
        setActiveChatId,
        setConversationStarted
    ]);
    const handleSelectChat = useCallback((chatId)=>{
        shanks.reset();
        setConversationStarted(true);
        setActiveChatId(chatId);
        setShowHistory(false);
    }, [
        shanks,
        setActiveChatId,
        setConversationStarted
    ]);
    if (showHistory) {
        return /*#__PURE__*/ _jsx("aside", {
            "data-testid": Testids.assistant.aiSidebarRoot,
            className: "flex h-full w-full min-w-0 flex-col border-l border-slate-200 bg-white",
            children: /*#__PURE__*/ _jsx(SidebarChatHistory, {
                onBack: ()=>setShowHistory(false),
                onSelectChat: handleSelectChat
            })
        });
    }
    if (historyLoading) {
        return /*#__PURE__*/ _jsxs("aside", {
            "data-testid": Testids.assistant.aiSidebarRoot,
            className: "flex h-full w-full min-w-0 flex-col items-center justify-center border-l border-slate-200 bg-white",
            children: [
                /*#__PURE__*/ _jsx(Loader2, {
                    className: "h-5 w-5 animate-spin text-slate-400"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-2 text-xs text-slate-500",
                    children: "Loading conversation..."
                })
            ]
        });
    }
    const hasMessages = messages.length > 0;
    // useShanksChatConnect (sabaody transport) exposes a single isLoading flag
    // rather than the express bridge's fine-grained streamingPhase; the only
    // sentinel tests wait on is chat-stream-idle (stream fully complete), so
    // derive idle-vs-active from isLoading.
    const phaseTestid = responseBusy ? Testids.assistant.chatStreamStreaming : Testids.assistant.chatStreamIdle;
    return /*#__PURE__*/ _jsxs("aside", {
        "data-testid": Testids.assistant.aiSidebarRoot,
        className: "flex h-full w-full min-w-0 flex-col border-l border-slate-200 bg-white",
        children: [
            /*#__PURE__*/ _jsx("span", {
                "data-testid": phaseTestid,
                "aria-hidden": "true",
                className: "hidden"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex min-w-0 items-center gap-2 text-sm font-medium text-slate-900",
                        children: [
                            /*#__PURE__*/ _jsx(Sparkles, {
                                className: "h-4 w-4 flex-shrink-0 text-violet-500",
                                "aria-hidden": "true"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "truncate",
                                children: "AI Assistant"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>setShowHistory(true),
                                className: "flex-shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700",
                                "aria-label": "Chat history",
                                title: "Chat history",
                                children: /*#__PURE__*/ _jsx(Clock, {
                                    className: "h-4 w-4"
                                })
                            }),
                            hasMessages && /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: handleNewChat,
                                className: "flex-shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700",
                                "aria-label": "New chat",
                                title: "New chat",
                                children: /*#__PURE__*/ _jsx(RotateCcw, {
                                    className: "h-4 w-4"
                                })
                            }),
                            !embedded && /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>setSidebarOpen(false),
                                className: "flex-shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700",
                                "aria-label": "Close AI sidebar",
                                children: /*#__PURE__*/ _jsx(X, {
                                    className: "h-4 w-4"
                                })
                            })
                        ]
                    })
                ]
            }),
            aiContext?.label && /*#__PURE__*/ _jsxs("div", {
                className: "border-b border-slate-100 px-4 py-2",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-[10px] uppercase tracking-wide text-slate-400",
                        children: "Asking about"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "truncate text-xs font-medium text-slate-700",
                        children: aiContext.label
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                ref: scrollRef,
                className: "flex-1 overflow-y-auto overflow-x-hidden",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "px-3 py-4",
                    children: [
                        history.isError ? /*#__PURE__*/ _jsx(ChatHistoryUnavailable, {
                            error: history.error,
                            onRetry: ()=>void history.refetch()
                        }) : null,
                        history.isError ? null : hasMessages ? /*#__PURE__*/ _jsx(ChatThread, {
                            messages: messages,
                            isSending: responseBusy,
                            chatId: activeChatId,
                            readOnly: readOnly,
                            onFeedback: sendCardFeedback
                        }) : /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col items-center justify-center py-12 text-center",
                            children: [
                                /*#__PURE__*/ _jsx(Sparkles, {
                                    className: "mx-auto h-6 w-6 text-slate-300",
                                    "aria-hidden": "true"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-2 text-sm font-medium text-slate-700",
                                    children: "Ask about this proposal"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-1 max-w-[220px] text-xs leading-relaxed text-slate-500",
                                    children: "I have context on your documents, CLINs, requirements, and more."
                                })
                            ]
                        }),
                        turnError ? /*#__PURE__*/ _jsx(ChatTurnError, {
                            error: turnError,
                            incomplete: incomplete,
                            onRetry: ()=>void shanks.retry()
                        }) : null,
                        awayFromBottom ? /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            size: "sm",
                            className: "sticky bottom-3 z-10",
                            onClick: jumpToLatest,
                            children: "Jump to latest"
                        }) : null
                    ]
                })
            }),
            !history.isError && !readOnly && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-slate-200 px-3 py-3",
                children: /*#__PURE__*/ _jsx(ComposerInput, {
                    onSubmit: handleSubmit,
                    submitting: responseBusy,
                    onStop: shanks.stop,
                    mode: "chat",
                    submitLabel: "Send"
                })
            })
        ]
    });
}
